import { useState, useEffect, useRef } from "react";
import { jwtFetch } from "./utils/apiClient";
import { format } from "date-fns";
import { Tabs, Tab, Modal } from "react-bootstrap";
import { config, priceFormat } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";

// Bulk Update CSV with the Invoice File column — off until rolled out; false keeps the original CSV flow.
const INVOICE_FILE_CSV_ENABLED = false;

// Quote-aware, so a quoted cell like "link1, link2" stays a single column.
const splitCsvLine = (line) => {
  const cells = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        cell += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      cells.push(cell);
      cell = '';
    } else {
      cell += char;
    }
  }
  cells.push(cell);
  return cells;
};

const toCsvCell = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const InvoiceRequest = () => {
  const [invoiceRequests, setInvoiceRequests] = useState(null);
  const [tableFilters, setTableFilters] = useState(null);
  const [tableSearch, setTableSearch] = useState('');
  const [tableSortBy, setTableSortBy] = useState('start_term_period');
  const [tableSortType, setTableSortType] = useState('ascending');
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [exportStart, setExportStart] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [exportEnd, setExportEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [exportStatus, setExportStatus] = useState('');
  const [exportQuotation, setExportQuotation] = useState('');
  const [exportSalesOrder, setExportSalesOrder] = useState('');
  const [bulkUpdateData, setBulkUpdateData] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState('');
  const searchInputRef = useRef(null);
  const latestRequestRef = useRef(0);

  const crmUser = JSON.parse(localStorage.getItem('crmUser'));

  useEffect(() => {
    setTableFilters({
      invoiceRequestStatus: 'Notification Sent',
      invoiceRequestCreatedBy: crmUser.user_level === 'User' && crmUser.user_department === 'Sales' ? [crmUser.user_keyid] : []
    });
    if (crmUser) {
      changeSelectedMenu(crmUser.user_department);
    }
  }, []);

  useEffect(() => {
    if (selectedMenu) {
      getInvoiceRequests();
    }
  }, [selectedMenu]);

  const getInvoiceRequests = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setIsLoadingPage(true);
      let requestInvoiceRequests = {};
      let invoiceRequestsEndpoint = 'get/iou_table';
      if (selectedMenu === 'Sales') {
        // Overdue + due within 7 days, filtered in SQL.
        invoiceRequestsEndpoint = 'get/iou_due_table';
        requestInvoiceRequests = {
          iou_statuses: ['New'],
          due_within_days: 7
        };
      } else if (selectedMenu === 'Sales Admin') {
        requestInvoiceRequests = {
          iou_statuses: ['In Progress']
        };
      } else if (selectedMenu === 'Finance') {
        requestInvoiceRequests = {
          iou_statuses: ['Notification Sent', 'Invoice Issued', 'Paid in Full', 'Cancelled']
        };
      }
      const fetchInvoiceRequests = await jwtFetch(config.ENDPOINT + invoiceRequestsEndpoint, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestInvoiceRequests)
      });
      const responseInvoiceRequests = await fetchInvoiceRequests.json();
      // A slower response for a menu the user already left must not overwrite the current menu's data.
      if (requestId !== latestRequestRef.current) return;
      if (responseInvoiceRequests.data) {
        const invoiceRequestsData = [];
        for (let i in responseInvoiceRequests.data) {
          let searchData = '';
          searchData += String(responseInvoiceRequests.data[i].iou_code ?? '').toLowerCase();
          searchData += String(responseInvoiceRequests.data[i].quotation_code ?? '').toLowerCase();
          searchData += String(responseInvoiceRequests.data[i].company_name ?? '').toLowerCase();
          searchData += String(responseInvoiceRequests.data[i].subcompany_name ?? '').toLowerCase();
          searchData += String(responseInvoiceRequests.data[i].sales_name ?? '').toLowerCase();
          searchData += String(responseInvoiceRequests.data[i].iou_status ?? '').toLowerCase();
          searchData += responseInvoiceRequests.data[i].invoice_number ? responseInvoiceRequests.data[i].invoice_number.toLowerCase() : '';
          searchData += responseInvoiceRequests.data[i].iou_item_names ? responseInvoiceRequests.data[i].iou_item_names.toLowerCase() : '';
          responseInvoiceRequests.data[i].search = searchData;
          invoiceRequestsData.push(responseInvoiceRequests.data[i]);
        }
        setInvoiceRequests(invoiceRequestsData);
        setIsLoadingPage(false);
      } else {
        alert(responseInvoiceRequests.message);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error('Error in getInvoiceRequests:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingPage(false);
    }
  }

  const filterTable = (key, value) => {
    let filters = { ...tableFilters };
    if (key === 'status') {
      filters.invoiceRequestStatus = value;
    }
    if (key === 'createdBy') {
      if (filters.invoiceRequestCreatedBy.includes(value)) {
        filters.invoiceRequestCreatedBy = filters.invoiceRequestCreatedBy.filter(item => item !== value);
      } else {
        filters.invoiceRequestCreatedBy.push(value);
      }
    }
    setTableFilters(filters);
  }

  const sortTable = (sortBy) => {
    if (tableSortBy === sortBy) {
      setTableSortType(tableSortType === 'ascending' ? 'descending' : 'ascending');
    } else {
      setTableSortBy(sortBy);
      setTableSortType('ascending');
    }
  }

  const clearSearch = () => {
    setTableSearch('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }

  const getStatusCount = (status) => {
    if (!invoiceRequests) return 0;
    return invoiceRequests.filter(invoiceRequest =>
      invoiceRequest.iou_status === status &&
      (tableSearch === '' || invoiceRequest.search.includes(tableSearch.toLowerCase())) &&
      (crmUser.user_level === 'User' && crmUser.user_department === 'Sales' ?
        [crmUser.user_keyid].includes(invoiceRequest.sales_keyid) :
        true
      )
    ).length;
  }

  const changeSelectedMenu = (menu) => {
    if (menu === 'Sales') {
      setSelectedMenu('Sales');
      sortTable('start_term_period');
      filterTable('status', 'New');
    } else if (menu === 'Sales Admin') {
      setSelectedMenu('Sales Admin');
      sortTable('start_term_period');
      filterTable('status', 'In Progress');
    } else {
      setSelectedMenu('Finance');
      filterTable('status', 'Notification Sent');
    }
  }

  const generateCsv = () => {
    const csvHeaders = [
      { label: "Request Number", key: "iou_code" },
      { label: "Invoice Number", key: "invoice_number" },
      { label: "Invoice Amount", key: "invoice_amount" },
      { label: "Due Date", key: "invoice_due_date" },
      { label: "Payment Date", key: "payment_paid_date" }
    ];

    if (!invoiceRequests) return;

    const data = invoiceRequests
      .filter(invoiceRequest => invoiceRequest.search.includes(tableSearch.toLowerCase()))
      .filter(invoiceRequest => {
        if (tableFilters && tableFilters.invoiceRequestStatus) {
          return invoiceRequest.iou_status === tableFilters.invoiceRequestStatus;
        }
        return true;
      })
      .filter(invoiceRequest => {
        if (tableFilters) {
          if (tableFilters.invoiceRequestCreatedBy && tableFilters.invoiceRequestCreatedBy.length > 0) {
            return tableFilters.invoiceRequestCreatedBy.includes(invoiceRequest.sales_keyid);
          }
          return true;
        }
        return true;
      })
      .sort((a, b) => {
        const aValue = a[tableSortBy];
        const bValue = b[tableSortBy];
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        const isNumeric = !isNaN(aNum) && !isNaN(bNum);
        if (isNumeric) {
          return tableSortType === 'ascending' ? aNum - bNum : bNum - aNum;
        }
        return tableSortType === 'ascending'
          ? (aValue ?? '').toString().localeCompare((bValue ?? '').toString())
          : (bValue ?? '').toString().localeCompare((aValue ?? '').toString());
      })
      .map(item => ({
        iou_code: item.iou_code,
        invoice_number: item.invoice_number,
        invoice_amount: item.invoice_amount,
        invoice_due_date: item.invoice_due_date ? format(new Date(item.invoice_due_date), 'd MMMM yyyy') : '',
        payment_paid_date: item.payment_paid_date ? format(new Date(item.payment_paid_date), 'd MMMM yyyy') : ''
      }));

    const headerRow = csvHeaders.map(h => h.label);
    const dataRows = data.map(row => csvHeaders.map(h => row[h.key]));

    const csvData = [headerRow, ...dataRows]
      .map(e => e.join(","))
      .join("\n");

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const timestamp = Math.floor(Date.now() / 1000);
    link.setAttribute('download', `invoice-requests-${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const uploadCsv = (file) => {
    const csvHeaders = [
      { label: "Request Number", key: "iou_code" },
      { label: "Invoice Number", key: "invoice_number" },
      { label: "Invoice Amount", key: "invoice_amount" },
      { label: "Due Date", key: "invoice_due_date" },
      { label: "Payment Date", key: "payment_paid_date" }
    ];

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r\n|\n/);
      const uploadedHeaders = lines[0].split(',').map(h => h.trim());
      const data = [];

      for (let i = 1; i < lines.length; i++) {
        if (!lines[i]) continue;
        const obj = {};
        const currentline = lines[i].split(',');
        for (let j = 0; j < uploadedHeaders.length; j++) {
          const headerLabel = uploadedHeaders[j];
          const headerMapping = csvHeaders.find(h => h.label === headerLabel);
          if (headerMapping) {
            let value = currentline[j].trim();
            if (headerMapping.key === 'payment_paid_date' && value) {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                value = `${year}-${month}-${day}`;
              } else {
                value = '';
              }
            }
            obj[headerMapping.key] = value;
          }
        }
        if (!obj.invoice_number) continue;

        const matchingRequest = invoiceRequests.find(req => req.iou_code === obj.iou_code);
        if (matchingRequest) {
          obj.id_iou = matchingRequest.id_iou;

          if (obj.invoice_number && !matchingRequest.invoice_number) {
            obj.iou_status = 'Invoice Issued';
            obj.invoice_input_date = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
            obj.invoice_input_by = crmUser.user_keyid;
          }
          if (obj.payment_paid_date && !matchingRequest.payment_paid_date) {
            obj.iou_status = 'Paid in Full';
            obj.payment_input_date = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
            obj.payment_input_by = crmUser.user_keyid;
          }
        }

        data.push(obj);
      }
      setBulkUpdateData(data);
    };
    reader.readAsText(file);
  };

  const updateBulk = async () => {
    try {
      setIsLoadingPage(true);
      if (!bulkUpdateData) {
        alert('Updated CSV file is required');
        setIsLoadingPage(false);
        return;
      }
      const requestUpdate = {
        ious: bulkUpdateData
      };
      const fetchUpdate = await jwtFetch(config.ENDPOINT + 'update/iou_bulk', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestUpdate)
      });
      const responseUpdate = await fetchUpdate.json();
      if (responseUpdate.status === 200) {
        window.location.reload();
      } else {
        setIsLoadingPage(false);
        alert(responseUpdate.message);
      }
    } catch (error) {
      console.error('Error in updateBulk:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingPage(false);
    }
  }

  const generateCsvWithInvoiceFile = () => {
    const csvHeaders = [
      { label: "Request Number", key: "iou_code" },
      { label: "Invoice Number", key: "invoice_number" },
      { label: "Invoice Amount", key: "invoice_amount" },
      { label: "Due Date", key: "invoice_due_date" },
      { label: "Payment Date", key: "payment_paid_date" },
      { label: "Invoice File", key: "invoice_attachments" }
    ];

    if (!invoiceRequests) return;

    const data = invoiceRequests
      .filter(invoiceRequest => invoiceRequest.search.includes(tableSearch.toLowerCase()))
      .filter(invoiceRequest => {
        if (tableFilters && tableFilters.invoiceRequestStatus) {
          return invoiceRequest.iou_status === tableFilters.invoiceRequestStatus;
        }
        return true;
      })
      .filter(invoiceRequest => {
        if (tableFilters) {
          if (tableFilters.invoiceRequestCreatedBy && tableFilters.invoiceRequestCreatedBy.length > 0) {
            return tableFilters.invoiceRequestCreatedBy.includes(invoiceRequest.sales_keyid);
          }
          return true;
        }
        return true;
      })
      .sort((a, b) => {
        const aValue = a[tableSortBy];
        const bValue = b[tableSortBy];
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        const isNumeric = !isNaN(aNum) && !isNaN(bNum);
        if (isNumeric) {
          return tableSortType === 'ascending' ? aNum - bNum : bNum - aNum;
        }
        return tableSortType === 'ascending'
          ? (aValue ?? '').toString().localeCompare((bValue ?? '').toString())
          : (bValue ?? '').toString().localeCompare((aValue ?? '').toString());
      })
      .map(item => ({
        iou_code: item.iou_code,
        invoice_number: item.invoice_number,
        invoice_amount: item.invoice_amount,
        invoice_due_date: item.invoice_due_date ? format(new Date(item.invoice_due_date), 'd MMMM yyyy') : '',
        payment_paid_date: item.payment_paid_date ? format(new Date(item.payment_paid_date), 'd MMMM yyyy') : '',
        invoice_attachments: item.invoice_attachments ? JSON.parse(item.invoice_attachments).map((attachment) => attachment.fileUrl).join(', ') : ''
      }));

    const headerRow = csvHeaders.map(h => h.label);
    const dataRows = data.map(row => csvHeaders.map(h => row[h.key]));

    const csvData = [headerRow, ...dataRows]
      .map(e => e.map(toCsvCell).join(","))
      .join("\n");

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const timestamp = Math.floor(Date.now() / 1000);
    link.setAttribute('download', `invoice-requests-${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const uploadCsvWithInvoiceFile = (file) => {
    const csvHeaders = [
      { label: "Request Number", key: "iou_code" },
      { label: "Invoice Number", key: "invoice_number" },
      { label: "Invoice Amount", key: "invoice_amount" },
      { label: "Due Date", key: "invoice_due_date" },
      { label: "Payment Date", key: "payment_paid_date" },
      { label: "Invoice File", key: "invoice_file_links" }
    ];

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r\n|\n/);
      const uploadedHeaders = splitCsvLine(lines[0]).map(h => h.trim());
      const data = [];

      for (let i = 1; i < lines.length; i++) {
        if (!lines[i]) continue;
        const obj = {};
        const currentline = splitCsvLine(lines[i]);
        for (let j = 0; j < uploadedHeaders.length; j++) {
          const headerLabel = uploadedHeaders[j];
          const headerMapping = csvHeaders.find(h => h.label === headerLabel);
          if (headerMapping) {
            let value = (currentline[j] ?? '').trim();
            if (headerMapping.key === 'invoice_file_links') {
              const links = value.split(',').map(link => link.trim()).filter(Boolean);
              if (links.length > 0) {
                obj.invoice_file_links = links;
              }
              continue;
            }
            if (headerMapping.key === 'payment_paid_date' && value) {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                value = `${year}-${month}-${day}`;
              } else {
                value = '';
              }
            }
            obj[headerMapping.key] = value;
          }
        }
        if (!obj.invoice_number && !obj.invoice_file_links) continue;

        const matchingRequest = invoiceRequests.find(req => req.iou_code === obj.iou_code);
        if (matchingRequest) {
          obj.id_iou = matchingRequest.id_iou;

          if (obj.invoice_number && !matchingRequest.invoice_number) {
            obj.iou_status = 'Invoice Issued';
            obj.invoice_input_date = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
            obj.invoice_input_by = crmUser.user_keyid;
          }
          if (obj.payment_paid_date && !matchingRequest.payment_paid_date) {
            obj.iou_status = 'Paid in Full';
            obj.payment_input_date = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
            obj.payment_input_by = crmUser.user_keyid;
          }
        }

        // An invoice file can't be replaced once attached — this also skips our
        // own Drive links when an exported file is uploaded back.
        if (!matchingRequest || matchingRequest.invoice_attachments) {
          delete obj.invoice_file_links;
        }

        if (!obj.invoice_number) {
          // File-only row: send nothing else, or its empty cells would clear
          // the request's existing invoice and payment fields.
          if (obj.invoice_file_links) {
            data.push({ id_iou: obj.id_iou, iou_code: obj.iou_code, invoice_file_links: obj.invoice_file_links });
          }
          continue;
        }

        data.push(obj);
      }
      setBulkUpdateData(data);
    };
    reader.readAsText(file);
  };

  const updateBulkWithInvoiceFile = async () => {
    try {
      setIsLoadingPage(true);
      if (!bulkUpdateData) {
        alert('Updated CSV file is required');
        setIsLoadingPage(false);
        return;
      }

      // One request per row: copying a Drive file takes seconds, so sending
      // the whole CSV in a single request would run into the proxy timeout.
      const failedRows = [];
      const ious = [];
      for (const row of bulkUpdateData) {
        const { invoice_file_links, ...iouData } = row;
        if (invoice_file_links) {
          try {
            const fetchUpload = await jwtFetch(config.ENDPOINT + 'upload/gdrive_link', {
              method: 'POST',
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ links: invoice_file_links })
            });
            const responseUpload = await fetchUpload.json();
            if (responseUpload.status === 200) {
              iouData.invoice_attachments = JSON.stringify(responseUpload.data);
            } else {
              failedRows.push(`${row.iou_code}: ${responseUpload.message}`);
            }
          } catch (error) {
            failedRows.push(`${row.iou_code}: ${error.message || error}`);
          }
          if (!iouData.invoice_attachments && !iouData.invoice_number) continue;
        }
        ious.push(iouData);
      }

      if (ious.length > 0) {
        const requestUpdate = {
          ious: ious
        };
        const fetchUpdate = await jwtFetch(config.ENDPOINT + 'update/iou_bulk', {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestUpdate)
        });
        const responseUpdate = await fetchUpdate.json();
        if (responseUpdate.status !== 200) {
          setIsLoadingPage(false);
          alert(responseUpdate.message);
          return;
        }
      }

      if (failedRows.length > 0) {
        alert('Invoice file failed to upload for these rows. Other columns in these rows were still saved — fix the link and upload the CSV again, or upload the file from the Invoice Request detail page.\n\n' + failedRows.join('\n'));
      }
      window.location.reload();
    } catch (error) {
      console.error('Error in updateBulk:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingPage(false);
    }
  }

  const exportCsv = async () => {
    try {
      const csvHeaders = [
        { label: "Invoice Request Number", key: "iou_code" },
        { label: "Schedule Number", key: "term_number" },
        { label: "Start Term Period", key: "start_term_period" },
        { label: "End Term Period", key: "end_term_period" },
        { label: "Currency", key: "iou_currency" },
        { label: "Amount", key: "iou_subtotal" },
        { label: "Status", key: "iou_status" },
        { label: "Company Name", key: "company_name" },
        { label: "Subsidiary Name", key: "subcompany_name" },
        { label: "Sales Name", key: "sales_name" },
        { label: "Quotation Number", key: "quotation_code" },
        { label: "Sales Order Number", key: "so_code" },
        { label: "Created On", key: "created_on" }
      ];

      setIsLoadingPage(true);

      let requestInvoiceRequests = {
        iou_statuses: [],
        // range_start: exportStart,
        // range_end: exportEnd
      };

      if (exportStatus) {
        requestInvoiceRequests.iou_statuses.push(exportStatus);
      }

      const fetchInvoiceRequests = await jwtFetch(config.ENDPOINT + 'get/iou_table', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestInvoiceRequests)
      });

      const responseInvoiceRequests = await fetchInvoiceRequests.json();
      if (responseInvoiceRequests.data) {
        const data = responseInvoiceRequests.data
          .filter(item => {
            if (exportQuotation && item.quotation_code !== exportQuotation) return false;
            if (exportSalesOrder && item.so_code !== exportSalesOrder) return false;
            return true;
          })
          .map(item => ({
            iou_code: item.iou_code,
            term_number: item.term_number.replace('Term', 'Schedule'),
            start_term_period: format(new Date(item.start_term_period), 'd MMMM yyyy'),
            end_term_period: format(new Date(item.end_term_period), 'd MMMM yyyy'),
            iou_currency: item.iou_currency,
            iou_subtotal: item.iou_subtotal,
            iou_status: (item.iou_status === 'New' ? 'Not Billed' : (item.iou_status === 'In Progress' ? 'Admin Validation' : item.iou_status)),
            company_name: item.company_name,
            subcompany_name: item.subcompany_name,
            sales_name: item.sales_name,
            quotation_code: item.quotation_code,
            so_code: item.so_code,
            created_on: format(new Date(item.created_on), 'd MMMM yyyy')
          }));

        const headerRow = csvHeaders.map(h => h.label);
        const dataRows = data.map(row => csvHeaders.map(h => row[h.key]));

        const csvData = [headerRow, ...dataRows]
          .map(e => e.join(","))
          .join("\n");

        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const timestamp = Math.floor(Date.now() / 1000);
        link.setAttribute('download', `invoice-requests-${timestamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setIsLoadingPage(false);
      } else {
        alert(responseInvoiceRequests.message);
      }
    } catch (error) {
      console.error('Error in exportCsv:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingPage(false);
    }
  };

  return (
    <div id="invoicerequest-list">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
            </div>
            <hr className="m-0" />
            <Menu active="invoicerequest" />
          </div>
          <div className="col-md-10">
            <Header />
            <hr className="m-0" />
            <div className="main">
              <div className="p-4">

                <section className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center justify-content-between gap-4">
                      <div className="fs-24">Invoice Request List</div>
                      <div className="btn-group" role="group">
                        <button className={"btn " + (selectedMenu === 'Sales' ? 'bg-gold' : 'btn-secondary')} type="button" onClick={() => changeSelectedMenu('Sales')}>Sales</button>
                        <button className={"btn " + (selectedMenu === 'Sales Admin' ? 'bg-gold' : 'btn-secondary')} type="button" onClick={() => changeSelectedMenu('Sales Admin')}>Sales Admin</button>
                        <button className={"btn " + (selectedMenu === 'Finance' ? 'bg-gold' : 'btn-secondary')} type="button" onClick={() => changeSelectedMenu('Finance')}>Finance</button>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <div className="input-group input-search">
                        <button className="btn bg-white" type="button" onClick={() => clearSearch()}>{tableSearch ? <i className="bi bi-x-lg" /> : <i className="bi bi-search" />}</button>
                        <input className="form-control" type="text" placeholder="Search" value={tableSearch} onChange={(e) => setTableSearch(e.target.value)} ref={searchInputRef} autoFocus />
                      </div>
                      {(selectedMenu === 'Finance' && crmUser.user_level !== 'View') && (
                        <button className="btn btn-link bg-gold" type="button" onClick={() => setShowUpload(true)}><i className="bi bi-upload me-1" /> Bulk Update</button>
                      )}
                      {!(crmUser.user_level === 'User' && crmUser.user_department === 'Sales') && (
                        <button className="btn btn-link bg-gold" type="button" onClick={() => setShowExport(true)}><i className="bi bi-download me-1" /> Export</button>
                      )}
                      {/* <button className="btn btn-link bg-gold" type="button" onClick={() => window.location.href = '/invoicerequest/add'}><i className="bi bi-plus-circle me-1"/> Add</button> */}
                    </div>
                  </div>
                  <div className="bg-white border-radius-4 p-4">
                    {selectedMenu === 'Sales' && (
                      <Tabs className="mb-3" defaultActiveKey="Overdue" onSelect={(key) => filterTable('status', key)}>
                        <Tab eventKey="Overdue" title={`Overdue (${tableSearch ? (getStatusCount('New') > 0 ? getStatusCount('New') : 0) : ((invoiceRequests && invoiceRequests.length) || 0)})`} />
                      </Tabs>
                    )}
                    {selectedMenu === 'Sales Admin' && (
                      <Tabs className="mb-3" defaultActiveKey="Admin Validation" onSelect={(key) => filterTable('status', key)}>
                        <Tab eventKey="Admin Validation" title={`Admin Validation (${tableSearch ? (getStatusCount('In Progress') > 0 ? getStatusCount('In Progress') : 0) : ((invoiceRequests && invoiceRequests.length) || 0)})`} />
                      </Tabs>
                    )}
                    {selectedMenu === 'Finance' && (
                      <Tabs className="mb-3" defaultActiveKey="Notification Sent" onSelect={(key) => filterTable('status', key)}>
                        <Tab eventKey="Notification Sent" title={`Requested to Finance${getStatusCount('Notification Sent') > 0 ? ` (${getStatusCount('Notification Sent')})` : ''}`} />
                        <Tab eventKey="Invoice Issued" title={`Invoice Issued${getStatusCount('Invoice Issued') > 0 ? ` (${getStatusCount('Invoice Issued')})` : ''}`} />
                        <Tab eventKey="Paid in Full" title={`Paid in Full${getStatusCount('Paid in Full') > 0 ? ` (${getStatusCount('Paid in Full')})` : ''}`} />
                        <Tab eventKey="Canceled" title={`Cancelled${getStatusCount('Canceled') > 0 ? ` (${getStatusCount('Canceled')})` : ''}`} />
                      </Tabs>
                    )}
                    {isLoadingPage ? (
                      <p>Loading...</p>
                    ) : (
                      invoiceRequests && invoiceRequests.length > 0 && (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th className={"bg-" + (selectedMenu === 'Sales' ? 'danger' : 'gold') + " w-40"}>#</th>
                                {(selectedMenu === 'Sales' || selectedMenu === 'Sales Admin') ? (
                                  <th className={"bg-" + (selectedMenu === 'Sales' ? 'danger' : 'gold') + " w-185 pointer text-start"} onClick={() => sortTable('quotation_code')}>Number {tableSortBy === 'quotation_code' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                                ) : (
                                  <th className={"bg-" + (selectedMenu === 'Sales' ? 'danger' : 'gold') + " w-185 pointer text-start"} onClick={() => sortTable('iou_code')}>Number {tableSortBy === 'iou_code' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                                )}
                                {/* {tableFilters?.invoiceRequestStatus !== 'New' && tableFilters?.invoiceRequestStatus !== 'In Progress' && tableFilters?.invoiceRequestStatus !== 'Notification Sent' && (
                                  <th className={"bg-" + (selectedMenu === 'Sales' ? 'danger' : 'gold') + " w-120 pointer"} onClick={() => sortTable('invoice_number')}>Invoice {tableSortBy === 'invoice_number' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                                )} */}
                                <th className={"bg-" + (selectedMenu === 'Sales' ? 'danger' : 'gold') + " w-160 pointer text-start"} onClick={() => sortTable('start_term_period')}>Period {tableSortBy === 'start_term_period' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                                <th className={"bg-" + (selectedMenu === 'Sales' ? 'danger' : 'gold') + " pointer text-start"} onClick={() => sortTable('company_name')}>Company Name {tableSortBy === 'company_name' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                                <th className={"bg-" + (selectedMenu === 'Sales' ? 'danger' : 'gold') + " w-130 pointer text-end"} onClick={() => sortTable('iou_subtotal')}>Amount {tableSortBy === 'iou_subtotal' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                                {(selectedMenu === 'Sales' || selectedMenu === 'Sales Admin') ? (
                                  <th className={"bg-" + (selectedMenu === 'Sales' ? 'danger' : 'gold') + " w-180 pointer text-start"} onClick={() => sortTable('sales_name')}>Sales Name {tableSortBy === 'sales_name' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                                ) : (
                                  <th className={"bg-" + (selectedMenu === 'Sales' ? 'danger' : 'gold') + " w-180 pointer text-start"} onClick={() => sortTable('request_invoice_date')}>Sales Name {tableSortBy === 'request_invoice_date' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                                )}
                                <th className={"bg-" + (selectedMenu === 'Sales' ? 'danger' : 'gold') + " w-40"}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...invoiceRequests]
                                .sort((a, b) => {
                                  const aValue = a[tableSortBy];
                                  const bValue = b[tableSortBy];
                                  const aNum = Number(aValue);
                                  const bNum = Number(bValue);
                                  const isNumeric = !isNaN(aNum) && !isNaN(bNum);
                                  if (isNumeric) {
                                    return tableSortType === 'ascending' ? aNum - bNum : bNum - aNum;
                                  }
                                  return tableSortType === 'ascending'
                                    ? (aValue ?? '').toString().localeCompare((bValue ?? '').toString())
                                    : (bValue ?? '').toString().localeCompare((aValue ?? '').toString());
                                })
                                .filter(invoiceRequest => invoiceRequest.search.includes(tableSearch.toLowerCase()))
                                .filter(invoiceRequest => {
                                  if (tableFilters && tableFilters.invoiceRequestStatus && selectedMenu === 'Finance') {
                                    return invoiceRequest.iou_status === tableFilters.invoiceRequestStatus;
                                  }
                                  return true;
                                })
                                .filter(invoiceRequest => {
                                  if (tableFilters) {
                                    if (tableFilters.invoiceRequestCreatedBy && tableFilters.invoiceRequestCreatedBy.length > 0) {
                                      return tableFilters.invoiceRequestCreatedBy.includes(invoiceRequest.sales_keyid);
                                    }
                                    return true;
                                  }
                                  return true;
                                })
                                .map((invoiceRequest, q) => (
                                  <tr key={"invoiceRequest-" + q}>
                                    <td valign="middle" align="center">{q + 1}</td>
                                    {(selectedMenu === 'Sales' || selectedMenu === 'Sales Admin') ? (
                                      <td valign="middle">{invoiceRequest.quotation_code}</td>
                                    ) : (
                                      <td valign="middle">
                                        {invoiceRequest.iou_code}
                                        {tableFilters?.invoiceRequestStatus !== 'New' && tableFilters?.invoiceRequestStatus !== 'In Progress' && tableFilters?.invoiceRequestStatus !== 'Notification Sent' && (
                                          <small>
                                            <br />
                                            {invoiceRequest.invoice_number}
                                          </small>
                                        )}
                                      </td>
                                    )}
                                    <td valign="middle">
                                      <strong>{invoiceRequest.term_number.replace('Term', 'Schedule')}</strong>
                                      <br />
                                      <small>{format(invoiceRequest.start_term_period, 'd MMM yyyy')} - {format(invoiceRequest.end_term_period, 'd MMM yyyy')}</small>
                                    </td>
                                    <td valign="middle">
                                      <strong>{invoiceRequest.company_name}</strong>
                                      <br />
                                      <small>{invoiceRequest.subcompany_billing_name}</small>
                                    </td>
                                    <td valign="middle" align="right">
                                      {priceFormat(invoiceRequest.iou_subtotal, invoiceRequest.iou_currency)}
                                      {invoiceRequest.invoice_amount && (
                                        <small>
                                          <br />
                                          {priceFormat(invoiceRequest.invoice_amount, invoiceRequest.iou_currency)}
                                        </small>
                                      )}
                                    </td>
                                    <td valign="middle">
                                      {invoiceRequest.sales_name}
                                      <br />
                                      {(selectedMenu === 'Sales' || selectedMenu === 'Sales Admin') ? (
                                        <small>Created on {format(invoiceRequest.created_on, 'd MMM yyyy')}</small>
                                      ) : (
                                        <small>Sent on {format(invoiceRequest.request_invoice_date, 'd MMM yyyy HH:mm')}</small>
                                      )}
                                    </td>
                                    {selectedMenu === 'Sales' && (
                                      <td valign="middle" align="right"><a className="btn btn-sm bg-danger" href={'/quotation/atb/' + invoiceRequest.quotation_id + '/' + invoiceRequest.term_number}><i className="bi bi-eye text-light" /></a></td>
                                    )}
                                    {selectedMenu === 'Sales Admin' && (
                                      <td valign="middle" align="right"><a className="btn btn-sm bg-gold" href={'/quotation/atb/detail/' + invoiceRequest.quotation_id + '/' + invoiceRequest.term_number}><i className="bi bi-eye" /></a></td>
                                    )}
                                    {selectedMenu === 'Finance' && (
                                      <td valign="middle" align="right"><a className="btn btn-sm bg-gold" href={'/invoicerequest/detail/' + invoiceRequest.id_iou}><i className="bi bi-eye" /></a></td>
                                    )}
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      )
                    )}
                  </div>
                </section>

              </div>
            </div>
          </div>

          <Modal show={showUpload} onHide={() => setShowUpload(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Bulk Update</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="row align-items-baseline form-group mb-2">
                <div className="col-md-4">
                  <label>Master data<span className="text-danger">*</span></label>
                </div>
                <div className="col-md-8">
                  <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => (INVOICE_FILE_CSV_ENABLED ? generateCsvWithInvoiceFile : generateCsv)(true)}><i className="bi bi-download me-1" /> Download file</button>
                </div>
              </div>
              <div className="row align-items-baseline form-group mb-2">
                <div className="col-md-4">
                  <label>Updated data<span className="text-danger">*</span></label>
                </div>
                <div className="col-md-8">
                  <input className="form-control" type="file" placeholder="Upload file" accept=".csv" onChange={(e) => (INVOICE_FILE_CSV_ENABLED ? uploadCsvWithInvoiceFile : uploadCsv)(e.target.files[0])} required />
                </div>
              </div>
              <div className="d-flex justify-content-end">
                <button className="btn btn-link" type="button" onClick={() => setShowUpload(false)} disabled={isLoadingPage}>Cancel</button>
                <button className="btn btn-link bg-gold" type="button" onClick={() => (INVOICE_FILE_CSV_ENABLED ? updateBulkWithInvoiceFile : updateBulk)()} disabled={isLoadingPage}>Update</button>
              </div>
            </Modal.Body>
          </Modal>

          <Modal show={showExport} onHide={() => setShowExport(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Export</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {/* <div className="row align-items-baseline form-group mb-2">
                <div className="col-md-4">
                  <label>Date Range<span className="text-danger">*</span></label>
                </div>
                <div className="col-md-4">
                  <input className="form-control" type="date" value={exportStart} onChange={(e) => setExportStart(e.target.value)}/>
                </div>
                <div className="col-md-4">
                  <input className="form-control" type="date" value={exportEnd} onChange={(e) => setExportEnd(e.target.value)}/>
                </div>
              </div> */}
              <div className="row align-items-baseline form-group mb-2">
                <div className="col-md-4">
                  <label>Quotation Number</label>
                </div>
                <div className="col-md-8">
                  <input className="form-control" type="text" value={exportQuotation} onChange={(e) => setExportQuotation(e.target.value)} />
                </div>
              </div>
              <div className="row align-items-baseline form-group mb-2">
                <div className="col-md-4">
                  <label>Sales Order Number</label>
                </div>
                <div className="col-md-8">
                  <input className="form-control" type="text" value={exportSalesOrder} onChange={(e) => setExportSalesOrder(e.target.value)} />
                </div>
              </div>
              <div className="row align-items-baseline form-group mb-2">
                <div className="col-md-4">
                  <label>Status</label>
                </div>
                <div className="col-md-8">
                  <select className="form-select" value={exportStatus} onChange={(e) => setExportStatus(e.target.value)}>
                    <option value="">All Statuses</option>
                    <option value="New">Not Billed</option>
                    <option value="In Progress">Admin Validation</option>
                    <option value="Notification Sent">Requested to Finance</option>
                    <option value="Invoice Issued">Invoice Issued</option>
                    <option value="Paid in Full">Paid in Full</option>
                    <option value="Stopped">Stopped</option>
                  </select>
                </div>
              </div>
              <div className="d-flex justify-content-end">
                <button className="btn btn-link" type="button" onClick={() => setShowExport(false)} disabled={isLoadingPage}>Cancel</button>
                <button className="btn btn-link bg-gold" type="button" onClick={() => exportCsv()} disabled={isLoadingPage}>Export</button>
              </div>
            </Modal.Body>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default InvoiceRequest;

